// Vendor data-layer client (NOT Supabase Auth). Mirrors lib/adminApi.ts but for
// the vendor portal: every privileged call posts the opaque vendor session token
// to a vendor-* edge function, which enforces ownership via is_venue_vendor_by_id.
//
// All vendor WRITES go through here (vendors have no auth.uid()/RLS session).
// Analytics reads and other non-public reads (event_costs, event_revenue_estimates,
// venue_running_costs, venue_business_registry) also go through here; table reads that
// still have public-read policies (events, ticket_types, venues) stay on the
// Supabase anon client in their respective lib modules.

import { getVendorToken } from "@/lib/vendorAuth";
import { isCookieVendorAuthMode, vendorApiUrl } from "@/lib/vendorAuthMode";

function supabaseUrl(): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new Error("Missing VITE_SUPABASE_URL");
  return url;
}

function supabaseAnonKey(): string {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) throw new Error("Missing VITE_SUPABASE_ANON_KEY");
  return key;
}

export type VendorFunctionName =
  | "vendor-events"
  | "vendor-venue"
  | "vendor-tickets"
  | "vendor-financials"
  | "vendor-ambiance-tags"
  | "vendor-analytics";

/** Low-level fetch: posts { token, action, ...params } and returns the JSON body. */
export async function vendorFetch<T>(
  functionName: VendorFunctionName,
  action: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  if (isCookieVendorAuthMode()) {
    const res = await fetch(vendorApiUrl(`/proxy/${functionName}`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...params }),
    });
    const body = (await res.json()) as { ok?: boolean; error?: string } & Record<string, unknown>;
    if (!res.ok || !body.ok) {
      throw new Error(body.error ?? "Request failed");
    }
    return body as T;
  }

  const token = getVendorToken();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${supabaseUrl()}/functions/v1/${functionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${supabaseAnonKey()}`,
      apikey: supabaseAnonKey(),
    },
    body: JSON.stringify({ token, action, ...params }),
  });

  const body = (await res.json()) as { ok?: boolean; error?: string } & Record<string, unknown>;
  if (!res.ok || !body.ok) {
    throw new Error(body.error ?? "Request failed");
  }
  return body as T;
}

type Row = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
export const vendorEventsApi = {
  list: (venueId: string) =>
    vendorFetch<{ events: Row[] }>("vendor-events", "list", { venueId }),
  create: (venueId: string, payload: Record<string, unknown>) =>
    vendorFetch<{ event: Row }>("vendor-events", "create", { venueId, ...payload }),
  update: (id: string, payload: Record<string, unknown>) =>
    vendorFetch<{ ok: true }>("vendor-events", "update", { id, ...payload }),
  remove: (id: string) => vendorFetch<{ ok: true }>("vendor-events", "delete", { id }),
  cancel: (id: string) => vendorFetch<{ ok: true }>("vendor-events", "cancel", { id }),
  publish: (id: string) => vendorFetch<{ ok: true }>("vendor-events", "publish", { id }),
  unpublish: (id: string) => vendorFetch<{ ok: true }>("vendor-events", "unpublish", { id }),
  setCrawlLocked: (id: string, locked: boolean) =>
    vendorFetch<{ ok: true }>("vendor-events", "set_crawl_locked", { id, locked }),
};

// ---------------------------------------------------------------------------
// Venue master data + business registry
// ---------------------------------------------------------------------------
export const vendorVenueApi = {
  get: (venueId: string) =>
    vendorFetch<{ venue: Row }>("vendor-venue", "get", { venueId }),
  update: (venueId: string, payload: Record<string, unknown>) =>
    vendorFetch<{ ok: true }>("vendor-venue", "update", { venueId, ...payload }),
  getRegistry: (venueId: string) =>
    vendorFetch<{ registry: Row | null }>("vendor-venue", "get_registry", { venueId }),
  saveRegistry: (venueId: string, form: Record<string, unknown>, status: string) =>
    vendorFetch<{ registry: Row }>("vendor-venue", "save_registry", { venueId, ...form, status }),
  submitRegistry: (venueId: string, form: Record<string, unknown>) =>
    vendorFetch<{ registry: Row }>("vendor-venue", "submit_registry", { venueId, ...form }),
  withdrawRegistry: (venueId: string) =>
    vendorFetch<{ registry: Row }>("vendor-venue", "withdraw_registry", { venueId }),
  resubmitRegistry: (venueId: string) =>
    vendorFetch<{ registry: Row }>("vendor-venue", "resubmit_registry", { venueId }),
  uploadPhoto: (venueId: string, fileBase64: string, contentType: string) =>
    vendorFetch<{ publicUrl: string; storagePath: string }>("vendor-venue", "upload_photo", {
      venueId,
      fileBase64,
      contentType,
    }),
  deletePhoto: (venueId: string, storagePath: string) =>
    vendorFetch<{ ok: true }>("vendor-venue", "delete_photo", { venueId, storagePath }),
};

// ---------------------------------------------------------------------------
// Ambiance tags (vendor review of suggested venue vibe tags)
// ---------------------------------------------------------------------------
export type VendorAmbianceTag = {
  id: string;
  venue_id: string;
  tag: string;
  status: "suggested" | "vendor_confirmed" | "vendor_rejected" | "confirmed" | "rejected";
  source: "crawler_enrichment" | "admin" | "vendor";
  confidence: number | null;
  evidence: string | null;
  created_at: string | null;
  confirmed_at: string | null;
  rejected_at: string | null;
};

export const vendorAmbianceTagsApi = {
  list: (venueId: string) =>
    vendorFetch<{ tags: VendorAmbianceTag[] }>("vendor-ambiance-tags", "list", { venueId }),
  confirm: (id: string) =>
    vendorFetch<{ ok: true }>("vendor-ambiance-tags", "confirm", { id }),
  reject: (id: string) =>
    vendorFetch<{ ok: true }>("vendor-ambiance-tags", "reject", { id }),
  suggest: (venueId: string, tag: string) =>
    vendorFetch<{ tag: VendorAmbianceTag }>("vendor-ambiance-tags", "suggest", { venueId, tag }),
};

// ---------------------------------------------------------------------------
// Analytics (cookie → edge → service_role RPCs; vendors have no auth.uid())
// ---------------------------------------------------------------------------
export const vendorAnalyticsApi = {
  engagementDaily: (venueId: string, days = 30) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "engagement_daily", { venueId, days }),
  engagementRows: (venueId: string, since?: string | null) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "engagement_rows", { venueId, since: since ?? null }),
  eventEngagementRows: (venueId: string, eventIds: string[], since?: string | null) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "event_engagement_rows", {
      venueId,
      eventIds,
      since: since ?? null,
    }),
  eventProposalRows: (venueId: string, eventIds: string[], since?: string | null) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "event_proposal_rows", {
      venueId,
      eventIds,
      since: since ?? null,
    }),
  ageDistribution: (venueId: string) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "age_distribution", { venueId }),
  ticketPriceStats: (venueId: string, venueIds: string[]) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "ticket_price_stats", { venueId, venueIds }),
  marketRadarPeerEvents: (venueId: string, venueIds: string[], since?: string | null) =>
    vendorFetch<{ rows: Row[] }>("vendor-analytics", "market_radar_peer_events", {
      venueId,
      venueIds,
      since: since ?? null,
    }),
  aiAssistantUsage: (venueId: string) =>
    vendorFetch<{ count: number }>("vendor-analytics", "ai_assistant_usage", { venueId }),
  interestCount: (venueId: string) =>
    vendorFetch<{ count: number }>("vendor-analytics", "interest_count", { venueId }),
};

// ---------------------------------------------------------------------------
// Ticket types
// ---------------------------------------------------------------------------
export const vendorTicketsApi = {
  list: (eventId: string) =>
    vendorFetch<{ ticketTypes: Row[] }>("vendor-tickets", "list", { eventId }),
  create: (eventId: string, payload: Record<string, unknown>) =>
    vendorFetch<{ ticketType: Row }>("vendor-tickets", "create", { eventId, ...payload }),
  update: (id: string, payload: Record<string, unknown>) =>
    vendorFetch<{ ok: true }>("vendor-tickets", "update", { id, ...payload }),
  remove: (id: string) => vendorFetch<{ ok: true }>("vendor-tickets", "delete", { id }),
  listSales: (venueId: string, eventIds?: string[]) =>
    vendorFetch<{ tickets: Row[] }>("vendor-tickets", "list_sales", { venueId, eventIds }),
  checkinsToday: (venueId: string) =>
    vendorFetch<{ count: number }>("vendor-tickets", "checkins_today", { venueId }),
  getForCheckin: (venueId: string, ticketId: string) =>
    vendorFetch<{ ticket: Row }>("vendor-tickets", "get_for_checkin", { venueId, ticketId }),
  redeem: (venueId: string, ticketId: string) =>
    vendorFetch<{ redeemedAt?: string; alreadyRedeemed?: boolean }>("vendor-tickets", "redeem", {
      venueId,
      ticketId,
    }),
};

// ---------------------------------------------------------------------------
// Financials (event costs, revenue estimates, running costs)
// ---------------------------------------------------------------------------
export const vendorFinancialsApi = {
  listCosts: (venueId: string) =>
    vendorFetch<{ costs: Row[] }>("vendor-financials", "list_costs", { venueId }),
  createCost: (payload: Record<string, unknown>) =>
    vendorFetch<{ cost: Row }>("vendor-financials", "create_cost", payload),
  updateCost: (id: string, payload: Record<string, unknown>) =>
    vendorFetch<{ cost: Row }>("vendor-financials", "update_cost", { id, ...payload }),
  deleteCost: (id: string) =>
    vendorFetch<{ ok: true }>("vendor-financials", "delete_cost", { id }),
  listRevenue: (venueId: string) =>
    vendorFetch<{ revenue: Row[] }>("vendor-financials", "list_revenue", { venueId }),
  upsertRevenue: (payload: Record<string, unknown>) =>
    vendorFetch<{ ok: true }>("vendor-financials", "upsert_revenue", payload),
  listRunningCosts: (venueId: string) =>
    vendorFetch<{ runningCosts: Row[] }>("vendor-financials", "list_running_costs", { venueId }),
  upsertRunningCosts: (payload: Record<string, unknown>) =>
    vendorFetch<{ runningCost: Row }>("vendor-financials", "upsert_running_costs", payload),
  deleteRunningCost: (id: string) =>
    vendorFetch<{ ok: true }>("vendor-financials", "delete_running_cost", { id }),
};
